import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, TypographyAppearanceVariants, TypographyTagVariants } from '@components/atoms/Typography';
import { RoundIcon, RoundIconVariants } from '@components/atoms/RoundIcon';
import { Container } from '@components/atoms/Container';
import styles from './ServiceGallery.module.css';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';

export interface ServiceGalleryProps {
    images: { src?: string; alt?: string }[];
    onEditClick?: () => void;
}

export const ServiceGallery: FC<ServiceGalleryProps> = ({ images, onEditClick }) => {
    const { t } = useTranslation();

    const [openLightbox, setOpenLightbox] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    const translations = {
        title: t('service-gallery-component.title'),
    };

    const slides = images.map(image => ({
        src: image.src || '', 
        alt: image.alt || '',
    }));

    const handleImageClick = (index: number) => {
        setCurrentImageIndex(index);
        setOpenLightbox(true);
    };

    return (
        <Container classes="px-0" customSize="custom-large">
            <div className="position-relative">
                <Typography
                    tagVariant={TypographyTagVariants.h2}
                    appearanceLevel={TypographyAppearanceVariants.level3}
                    classes="mb-4">
                    {translations.title}
                </Typography>

                {onEditClick && (
                    <div className="p-0 mt-2 position-absolute end-0 top-0 text-decoration-none">
                        <RoundIcon icon="fa-light fa-pen-to-square" variant={RoundIconVariants.variant2} onClick={onEditClick} />
                    </div>
                )}

                <div className={`${styles['service-gallery']} pt-2`} data-testid="service-gallery">
                    {images.map((image, index) => (
                        <img
                            key={`image-${index}`}
                            src={image.src}
                            alt={image.alt}
                            onClick={() => handleImageClick(index)}
                            style={{ cursor: 'pointer' }}
                        />
                    ))}
                </div>
            </div>

            <Lightbox
                open={openLightbox}
                close={() => setOpenLightbox(false)}
                slides={slides}
                index={currentImageIndex}
                toolbar={{
                    buttons: ['close'],
                }}
                controller={{
                    closeOnBackdropClick: true,
                }}
            />
        </Container>
    );
};
